
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Profile {
  id: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  disciplines: string | null;
  years_experience: number | null;
  organisatie: string | null;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  awarded_at: string;
}

interface Statistics {
  total_floor_plans: number;
  total_shared_floor_plans: number;
  total_favorites: number;
  average_rating: number;
}

export function useProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user?.id)
        .single();

      if (error) throw error;
      return data as Profile;
    },
    enabled: !!user,
  });

  const { data: badges = [] } = useQuery({
    queryKey: ['badges', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_badges")
        .select(`
          id,
          badges (
            name,
            description
          ),
          awarded_at
        `)
        .eq("user_id", user?.id);

      if (error) throw error;
      return data.map((item: any) => ({
        id: item.id,
        name: item.badges.name,
        description: item.badges.description,
        awarded_at: item.awarded_at,
      })) as Badge[];
    },
    enabled: !!user,
  });

  const { data: stats = {
    total_floor_plans: 0,
    total_shared_floor_plans: 0,
    total_favorites: 0,
    average_rating: 0,
  }} = useQuery({
    queryKey: ['trainer-stats', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainer_statistics")
        .select("*")
        .eq("user_id", user?.id)
        .single();

      if (error) throw error;
      return data as Statistics;
    },
    enabled: !!user,
  });

  const handleUpdateProfile = async (formData: any) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: formData.displayName,
          disciplines: formData.disciplines,
          organisatie: formData.organisatie,
          years_experience: parseInt(formData.yearsExperience),
          bio: formData.bio,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user?.id);

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: ['profile', user?.id]
      });

      toast({
        title: "Profiel bijgewerkt",
        description: "Je profiel is succesvol bijgewerkt.",
      });

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Fout bij bijwerken",
        description: "Er is een fout opgetreden bij het bijwerken van je profiel.",
        variant: "destructive",
      });
    }
  };

  const handleAvatarUpload = async (url: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          avatar_url: url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user?.id);

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: ['profile', user?.id]
      });

      toast({
        title: "Profielfoto bijgewerkt",
        description: "Je profielfoto is succesvol bijgewerkt.",
      });
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast({
        title: "Fout bij uploaden",
        description: "Er is een fout opgetreden bij het uploaden van je profielfoto.",
        variant: "destructive",
      });
    }
  };

  return {
    profile,
    badges,
    stats,
    isEditDialogOpen,
    setIsEditDialogOpen,
    handleUpdateProfile,
    handleAvatarUpload,
  };
}
